import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { AppToolbarBreadcrumb } from '../utils/navigationBreadcrumbs';

export interface AppToolbarPageState {
  /** Appended after the route-based navigation trail. */
  breadcrumbSuffix: AppToolbarBreadcrumb[];
  /** Tenant/site/floor pickers and similar controls for the secondary toolbar row. */
  contextControls: React.ReactNode | null;
  /** Page-specific actions shown in the primary toolbar (right side, before global actions). */
  pageActions: React.ReactNode | null;
}

const EMPTY_TOOLBAR_STATE: AppToolbarPageState = {
  breadcrumbSuffix: [],
  contextControls: null,
  pageActions: null,
};

const EMPTY_BREADCRUMB_SUFFIX: AppToolbarBreadcrumb[] = [];

function breadcrumbsContentEqual(
  a: AppToolbarBreadcrumb[],
  b: AppToolbarBreadcrumb[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (crumb, index) =>
      crumb.label === b[index].label && crumb.href === b[index].href,
  );
}

function pageStateEqual(a: AppToolbarPageState, b: AppToolbarPageState): boolean {
  return (
    breadcrumbsContentEqual(a.breadcrumbSuffix, b.breadcrumbSuffix) &&
    a.contextControls === b.contextControls &&
    a.pageActions === b.pageActions
  );
}

interface AppToolbarStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => AppToolbarPageState;
  setPageState: (patch: Partial<AppToolbarPageState>) => void;
  resetPageState: () => void;
}

function createAppToolbarStore(): AppToolbarStore {
  let snapshot = EMPTY_TOOLBAR_STATE;
  const listeners = new Set<() => void>();

  const notify = (): void => {
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot() {
      return snapshot;
    },
    setPageState(patch) {
      const nextBreadcrumbSuffix = patch.breadcrumbSuffix ?? snapshot.breadcrumbSuffix;
      const nextContextControls =
        patch.contextControls !== undefined ? patch.contextControls : snapshot.contextControls;
      const nextPageActions =
        patch.pageActions !== undefined ? patch.pageActions : snapshot.pageActions;

      const next: AppToolbarPageState = {
        breadcrumbSuffix: nextBreadcrumbSuffix,
        contextControls: nextContextControls,
        pageActions: nextPageActions,
      };

      if (pageStateEqual(snapshot, next)) {
        return;
      }

      snapshot = next;
      notify();
    },
    resetPageState() {
      if (pageStateEqual(snapshot, EMPTY_TOOLBAR_STATE)) {
        return;
      }
      snapshot = EMPTY_TOOLBAR_STATE;
      notify();
    },
  };
}

interface AppToolbarContextValue {
  subscribePageState: AppToolbarStore['subscribe'];
  getPageStateSnapshot: AppToolbarStore['getSnapshot'];
  setPageState: AppToolbarStore['setPageState'];
  resetPageState: AppToolbarStore['resetPageState'];
  secondaryToolbarActive: boolean;
  setSecondaryToolbarActive: (active: boolean) => void;
}

const AppToolbarContext = createContext<AppToolbarContextValue | null>(null);

export const AppToolbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeRef = useRef<AppToolbarStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createAppToolbarStore();
  }
  const store = storeRef.current;

  const [secondaryToolbarActive, setSecondaryToolbarActive] = useState(false);

  const value = useMemo(
    (): AppToolbarContextValue => ({
      subscribePageState: store.subscribe,
      getPageStateSnapshot: store.getSnapshot,
      setPageState: store.setPageState,
      resetPageState: store.resetPageState,
      secondaryToolbarActive,
      setSecondaryToolbarActive,
    }),
    [secondaryToolbarActive, store],
  );

  return <AppToolbarContext.Provider value={value}>{children}</AppToolbarContext.Provider>;
};

export function useAppToolbarPageState(): AppToolbarPageState {
  const { subscribePageState, getPageStateSnapshot } = useAppToolbarContext();
  return useSyncExternalStore(subscribePageState, getPageStateSnapshot, getPageStateSnapshot);
}

export function useAppToolbarContext(): AppToolbarContextValue {
  const ctx = useContext(AppToolbarContext);
  if (!ctx) {
    throw new Error('useAppToolbarContext must be used within AppToolbarProvider');
  }
  return ctx;
}

export interface UseAppToolbarOptions {
  breadcrumbSuffix?: AppToolbarBreadcrumb[];
  contextControls?: React.ReactNode | null;
  pageActions?: React.ReactNode | null;
}

/**
 * Register page-specific toolbar content. Cleared automatically on unmount.
 */
export function useAppToolbar(options: UseAppToolbarOptions): void {
  const { setPageState, resetPageState } = useAppToolbarContext();
  const { breadcrumbSuffix, contextControls, pageActions } = options;

  useLayoutEffect(() => {
    setPageState({
      breadcrumbSuffix: breadcrumbSuffix ?? EMPTY_BREADCRUMB_SUFFIX,
      contextControls: contextControls ?? null,
      pageActions: pageActions ?? null,
    });
  });

  useEffect(() => {
    return () => resetPageState();
  }, [resetPageState]);
}

/** Height constants for app shell layout offset calculations. */
export const APP_TOOLBAR_PRIMARY_HEIGHT = 64;
export const APP_TOOLBAR_SECONDARY_HEIGHT = 48;

export function useAppToolbarOffset(): number {
  const { secondaryToolbarActive } = useAppToolbarContext();
  return APP_TOOLBAR_PRIMARY_HEIGHT + (secondaryToolbarActive ? APP_TOOLBAR_SECONDARY_HEIGHT : 0);
}
